import { detectCards, detectNameBox } from "./detect";
import { segmentationProfile } from "./device";
import { finishAlphaMatte } from "./matte";
import type {
  CardRect,
  NameBox,
  Point,
  PreparedCard,
  PreparedProject,
  ProgressUpdate,
} from "../types";

type WorkerResult = {
  masks: ArrayBuffer[];
  modelMode: "isnet-int8" | "classical";
  elapsedMs: number;
};

type PendingRequest = {
  resolve: (result: WorkerResult) => void;
  reject: (error: Error) => void;
  onProgress: (progress: number, message: string) => void;
  resetWatchdog: () => void;
  clearWatchdog: () => void;
};

let requestId = 0;
let segmentWorker: Worker | null = null;
const pending = new Map<number, PendingRequest>();

function getWorker() {
  if (segmentWorker) return segmentWorker;
  segmentWorker = new Worker(
    new URL("../workers/segmentation.worker.ts", import.meta.url),
    { type: "module" },
  );
  segmentWorker.onmessage = (event) => {
    const message = event.data as {
      id: number;
      type: "progress" | "result" | "error";
      progress?: number;
      message?: string;
      masks?: ArrayBuffer[];
      modelMode?: "isnet-int8" | "classical";
      elapsedMs?: number;
    };
    const request = pending.get(message.id);
    if (!request) return;
    if (message.type === "progress") {
      request.resetWatchdog();
      request.onProgress(message.progress ?? 0, message.message ?? "");
      return;
    }
    pending.delete(message.id);
    request.clearWatchdog();
    if (message.type === "error") {
      request.reject(new Error(message.message || "高精度な切り抜きを完了できませんでした"));
      return;
    }
    request.resolve({
      masks: message.masks ?? [],
      modelMode: message.modelMode ?? "classical",
      elapsedMs: message.elapsedMs ?? 0,
    });
  };
  segmentWorker.onerror = (event) => {
    for (const request of pending.values()) {
      request.clearWatchdog();
      request.reject(new Error(event.message || "画像の仕上げを開始できませんでした"));
    }
    pending.clear();
    segmentWorker?.terminate();
    segmentWorker = null;
  };
  return segmentWorker;
}

function segmentCardsOnce(
  cards: HTMLCanvasElement[],
  onProgress: PendingRequest["onProgress"],
  profile: "default" | "constrained",
) {
  const id = ++requestId;
  const externalModelUrl = import.meta.env.VITE_SEGMENTATION_MODEL_URL;
  const modelSource = externalModelUrl
    ? { kind: "single" as const, url: externalModelUrl }
    : {
        kind: "chunks" as const,
        manifestUrl: `${import.meta.env.BASE_URL}models/isnetis-qoperator-u8s8.manifest.json`,
      };
  const payload = cards.map((card) => {
    const context = card.getContext("2d", { willReadFrequently: true });
    if (!context) throw new Error("カード画像を読み取れませんでした");
    const image = context.getImageData(0, 0, card.width, card.height);
    return { width: card.width, height: card.height, pixels: image.data.buffer };
  });
  const transfers = payload.map((card) => card.pixels);
  return new Promise<WorkerResult>((resolve, reject) => {
    let watchdog = 0;
    const clearWatchdog = () => globalThis.clearTimeout(watchdog);
    const resetWatchdog = () => {
      clearWatchdog();
      watchdog = globalThis.setTimeout(() => {
        if (!pending.delete(id)) return;
        segmentWorker?.terminate();
        segmentWorker = null;
        reject(new Error(
          "高精度な切り抜きに時間がかかりすぎたため中断しました。Safariのほかのタブやアプリを閉じて、もう一度お試しください。",
        ));
      }, profile === "constrained" ? 90_000 : 45_000);
    };
    pending.set(id, { resolve, reject, onProgress, resetWatchdog, clearWatchdog });
    resetWatchdog();
    getWorker().postMessage(
      { id, type: "segment", profile, modelSource, cards: payload },
      transfers,
    );
  });
}

function segmentCards(
  cards: HTMLCanvasElement[],
  onProgress: PendingRequest["onProgress"],
) {
  return segmentCardsOnce(cards, onProgress, segmentationProfile());
}

function canvas(width: number, height: number) {
  const result = document.createElement("canvas");
  result.width = Math.max(1, Math.round(width));
  result.height = Math.max(1, Math.round(height));
  return result;
}

function highQuality(context: CanvasRenderingContext2D) {
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
}

function cropCard(source: HTMLCanvasElement, rect: CardRect) {
  const result = canvas(rect.sw, rect.sh);
  const context = result.getContext("2d");
  if (!context) throw new Error("カードを切り出せませんでした");
  highQuality(context);
  context.drawImage(source, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, result.width, result.height);
  return result;
}

function fieldColors(card: HTMLCanvasElement) {
  const context = card.getContext("2d", { willReadFrequently: true });
  if (!context) return { base: [240, 240, 240], stripe: [220, 220, 223] };
  const { data } = context.getImageData(0, 0, card.width, card.height);
  const samples: Array<[number, number, number, number]> = [];
  for (let y = 0; y < card.height; y += 4) {
    // Sample only the neutral UI field. The right half contains the character,
    // whose pale costume used to skew the generated patch toward another hue.
    for (
      let x = Math.round(card.width * 0.27);
      x < Math.round(card.width * 0.55);
      x += 4
    ) {
      const offset = (y * card.width + x) * 4;
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const high = Math.max(r, g, b);
      const low = Math.min(r, g, b);
      if (low > 158 && high - low < 42) samples.push([r + g + b, r, g, b]);
    }
  }
  if (samples.length < 80) return { base: [240, 240, 240], stripe: [220, 220, 223] };
  samples.sort((a, b) => a[0] - b[0]);
  const averageRange = (start: number, end: number): [number, number, number] => {
    const from = Math.floor(samples.length * start);
    const to = Math.max(from + 1, Math.floor(samples.length * end));
    const totals = [0, 0, 0];
    for (let index = from; index < to; index += 1) {
      totals[0] += samples[index][1];
      totals[1] += samples[index][2];
      totals[2] += samples[index][3];
    }
    const count = to - from;
    return totals.map((value) => Math.round(value / count)) as [number, number, number];
  };
  return {
    base: averageRange(0.62, 0.82),
    stripe: averageRange(0.16, 0.34),
  };
}

function drawField(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  colors: ReturnType<typeof fieldColors>,
  clip?: NameBox,
) {
  context.save();
  if (clip) {
    context.beginPath();
    context.rect(
      clip.left * width,
      clip.top * height,
      (clip.right - clip.left) * width,
      (clip.bottom - clip.top) * height,
    );
    context.clip();
  }
  context.fillStyle = `rgb(${colors.base.join(" ")})`;
  context.fillRect(0, 0, width, height);
  context.strokeStyle = `rgb(${colors.stripe.join(" ")} / .56)`;
  context.lineWidth = Math.max(1, width / 520);
  const spacing = Math.max(9, width / 60);
  for (let offset = -height; offset < width + height; offset += spacing) {
    context.beginPath();
    context.moveTo(offset, height);
    context.lineTo(offset + height * 0.58, 0);
    context.stroke();
  }
  context.restore();
}

function featheredMask(width: number, height: number, box: NameBox) {
  const result = canvas(width, height);
  const context = result.getContext("2d");
  if (!context) return result;
  const left = box.left * width;
  const top = box.top * height;
  const right = box.right * width;
  const bottom = box.bottom * height;
  const boxWidth = Math.max(1, right - left);
  const boxHeight = Math.max(1, bottom - top);
  const fadeX = Math.min(width * 0.007, boxWidth * 0.08) / boxWidth;
  const fadeY = Math.min(height * 0.02, boxHeight * 0.12) / boxHeight;

  const horizontal = context.createLinearGradient(left, 0, right, 0);
  horizontal.addColorStop(0, "transparent");
  horizontal.addColorStop(fadeX, "#fff");
  horizontal.addColorStop(1 - fadeX, "#fff");
  horizontal.addColorStop(1, "transparent");
  context.fillStyle = horizontal;
  context.fillRect(left, top, boxWidth, boxHeight);

  const vertical = context.createLinearGradient(0, top, 0, bottom);
  vertical.addColorStop(0, "transparent");
  vertical.addColorStop(fadeY, "#fff");
  vertical.addColorStop(1 - fadeY, "#fff");
  vertical.addColorStop(1, "transparent");
  context.globalCompositeOperation = "destination-in";
  context.fillStyle = vertical;
  context.fillRect(left, top, boxWidth, boxHeight);
  return result;
}

function nameInkData(card: HTMLCanvasElement, nameBox: NameBox) {
  const sourceContext = card.getContext("2d", { willReadFrequently: true });
  if (!sourceContext) throw new Error("名前を読み取れませんでした");
  const source = sourceContext.getImageData(0, 0, card.width, card.height);
  const core = new Uint8Array(card.width * card.height);
  const radius = Math.max(1, Math.min(3, Math.round(card.width / 560)));
  const coreLeft = Math.max(0, Math.floor(nameBox.left * card.width));
  const coreTop = Math.max(0, Math.floor(nameBox.top * card.height));
  const coreRight = Math.min(card.width, Math.ceil(nameBox.right * card.width));
  const coreBottom = Math.min(card.height, Math.ceil(nameBox.bottom * card.height));

  for (let y = coreTop; y < coreBottom; y += 1) {
    for (let x = coreLeft; x < coreRight; x += 1) {
      const offset = (y * card.width + x) * 4;
      const gray = source.data[offset] * 0.299
        + source.data[offset + 1] * 0.587
        + source.data[offset + 2] * 0.114;
      if (gray < 158) core[y * card.width + x] = 1;
    }
  }

  const left = Math.max(0, coreLeft - radius);
  const top = Math.max(0, coreTop - radius);
  const right = Math.min(card.width, coreRight + radius);
  const bottom = Math.min(card.height, coreBottom + radius);
  const mask = new Uint8Array(card.width * card.height);
  for (let y = top; y < bottom; y += 1) {
    for (let x = left; x < right; x += 1) {
      for (let dy = -radius; dy <= radius && !mask[y * card.width + x]; dy += 1) {
        const sampleY = y + dy;
        if (sampleY < coreTop || sampleY >= coreBottom) continue;
        for (let dx = -radius; dx <= radius; dx += 1) {
          const sampleX = x + dx;
          if (
            sampleX >= coreLeft
            && sampleX < coreRight
            && core[sampleY * card.width + sampleX]
          ) {
            mask[y * card.width + x] = 1;
            break;
          }
        }
      }
    }
  }
  return { source, mask, left, top, right, bottom, radius };
}

function inpaintName(
  card: HTMLCanvasElement,
  ink: ReturnType<typeof nameInkData>,
  colors: ReturnType<typeof fieldColors>,
) {
  const output = new ImageData(
    new Uint8ClampedArray(ink.source.data),
    card.width,
    card.height,
  );
  const maxStep = Math.max(24, Math.round(card.height * 0.38));
  const isNeutral = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= card.width || y >= card.height) return false;
    const index = y * card.width + x;
    if (ink.mask[index]) return false;
    const offset = index * 4;
    const r = ink.source.data[offset];
    const g = ink.source.data[offset + 1];
    const b = ink.source.data[offset + 2];
    return Math.min(r, g, b) > 172 && Math.max(r, g, b) - Math.min(r, g, b) < 42;
  };

  for (let y = ink.top; y < ink.bottom; y += 1) {
    for (let x = ink.left; x < ink.right; x += 1) {
      if (!ink.mask[y * card.width + x]) continue;
      let sampleX = -1;
      let sampleY = -1;
      for (let step = ink.radius + 2; step <= maxStep; step += 1) {
        const diagonal = Math.round(step * 0.58);
        const candidates: Array<[number, number]> = [
          [x - diagonal, y + step],
          [x + diagonal, y - step],
          [x + step, y],
          [x - step, y],
        ];
        const found = candidates.find(([candidateX, candidateY]) =>
          isNeutral(candidateX, candidateY)
        );
        if (found) {
          [sampleX, sampleY] = found;
          break;
        }
      }
      const offset = (y * card.width + x) * 4;
      if (sampleX >= 0) {
        const sampleOffset = (sampleY * card.width + sampleX) * 4;
        output.data[offset] = ink.source.data[sampleOffset];
        output.data[offset + 1] = ink.source.data[sampleOffset + 1];
        output.data[offset + 2] = ink.source.data[sampleOffset + 2];
      } else {
        output.data[offset] = colors.base[0];
        output.data[offset + 1] = colors.base[1];
        output.data[offset + 2] = colors.base[2];
      }
      output.data[offset + 3] = 255;
    }
  }
  return output;
}

function createPlateBase(card: HTMLCanvasElement, nameBox: NameBox) {
  const result = canvas(card.width, card.height);
  const context = result.getContext("2d");
  if (!context) throw new Error("プレートを準備できませんでした");
  highQuality(context);
  const colors = fieldColors(card);
  drawField(context, result.width, result.height, colors);

  // Keep icons and role UI as one continuous source surface. The name itself
  // is restored later as a small, coordinate-perfect layer when it is visible.
  const retainedRight = 0.56;
  const uiWidth = Math.round(card.width * retainedRight);
  const retained = canvas(uiWidth, card.height);
  const retainedContext = retained.getContext("2d");
  if (!retainedContext) throw new Error("プレートUIを準備できませんでした");
  highQuality(retainedContext);
  retainedContext.drawImage(card, 0, 0, uiWidth, card.height, 0, 0, uiWidth, card.height);
  retainedContext.globalCompositeOperation = "destination-in";
  const fadeStart = uiWidth * 0.76;
  const gradient = retainedContext.createLinearGradient(fadeStart, 0, uiWidth, 0);
  gradient.addColorStop(0, "#fff");
  gradient.addColorStop(1, "transparent");
  retainedContext.fillStyle = gradient;
  retainedContext.fillRect(0, 0, uiWidth, card.height);
  context.drawImage(retained, 0, 0);

  const ink = nameInkData(card, nameBox);
  const cleanBox = {
    left: Math.max(0.215, nameBox.left - 0.022),
    top: Math.max(0.025, nameBox.top - 0.025),
    right: Math.min(0.73, nameBox.right + 0.03),
    bottom: Math.min(0.405, nameBox.bottom + 0.026),
  };
  const cleanPatch = canvas(result.width, result.height);
  const cleanContext = cleanPatch.getContext("2d", { willReadFrequently: true });
  if (cleanContext) {
    cleanContext.putImageData(inpaintName(card, ink, colors), 0, 0);
    cleanContext.globalCompositeOperation = "destination-in";
    cleanContext.drawImage(featheredMask(cleanPatch.width, cleanPatch.height, cleanBox), 0, 0);
    context.drawImage(cleanPatch, 0, 0);
  }
  return result;
}

export function createBaseCard(card: HTMLCanvasElement, nameBox: NameBox) {
  return createPlateBase(card, nameBox);
}

// Kept under the existing property name for backwards-compatible session
// storage. Copy the original rasterized glyph pixels as-is. The small halo
// around each dark core retains the game's own antialiasing instead of
// thresholding/re-typesetting the name.
export function createNameLayer(card: HTMLCanvasElement, nameBox: NameBox) {
  const result = canvas(card.width, card.height);
  const context = result.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("名前を準備できませんでした");
  const ink = nameInkData(card, nameBox);
  const output = context.createImageData(card.width, card.height);
  for (let y = ink.top; y < ink.bottom; y += 1) {
    for (let x = ink.left; x < ink.right; x += 1) {
      if (!ink.mask[y * card.width + x]) continue;
      const offset = (y * card.width + x) * 4;
      output.data[offset] = ink.source.data[offset];
      output.data[offset + 1] = ink.source.data[offset + 1];
      output.data[offset + 2] = ink.source.data[offset + 2];
      output.data[offset + 3] = 255;
    }
  }
  context.putImageData(output, 0, 0);
  return result;
}

function findAlphaBounds(alpha: Uint8ClampedArray, width: number, height: number, cropLeft: number) {
  let left = width;
  let top = height;
  let right = 0;
  let bottom = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = cropLeft; x < width; x += 1) {
      if (alpha[y * width + x] <= 18) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x + 1);
      bottom = Math.max(bottom, y + 1);
    }
  }
  if (right <= left || bottom <= top) return { left: cropLeft, top: 0, right: width, bottom: height };
  const sidePadding = Math.max(6, Math.round(height * 0.035));
  const topPadding = Math.max(6, Math.round(height * 0.025));
  return {
    left: Math.max(cropLeft, left - sidePadding),
    top: Math.max(0, top - topPadding),
    right: Math.min(width, right + sidePadding),
    // The visible silhouette — not transparent padding — should sit on the plate edge.
    bottom: Math.min(height, bottom),
  };
}

function findHead(alpha: Uint8ClampedArray, width: number, height: number): Point {
  const searchLeft = Math.floor(width * 0.32);
  const searchRight = Math.floor(width * 0.97);
  const maxY = Math.floor(height * 0.58);
  for (let y = 0; y < maxY; y += 1) {
    let start = -1;
    let best: [number, number] | null = null;
    for (let x = searchLeft; x <= searchRight; x += 1) {
      const active = x < searchRight && alpha[y * width + x] > 92;
      if (active && start < 0) start = x;
      if (start >= 0 && !active) {
        const span = x - start;
        if (span >= width * 0.12 && span <= width * 0.62) best = [start, x];
        start = -1;
      }
    }
    if (best) return { x: (best[0] + best[1]) / 2 / width, y: Math.min(0.42, (y + height * 0.11) / height) };
  }
  return { x: 0.7, y: 0.2 };
}

function makeHero(card: HTMLCanvasElement, fullAlpha: Uint8ClampedArray, nameBox: NameBox) {
  const cropLeft = Math.floor(card.width * 0.49);
  const alpha = new Uint8ClampedArray(fullAlpha);
  const clearRight = Math.min(card.width, Math.ceil((nameBox.right + 0.035) * card.width));
  const clearBottom = Math.min(card.height, Math.ceil((nameBox.bottom + 0.018) * card.height));
  for (let y = 0; y < clearBottom; y += 1) {
    for (let x = cropLeft; x < clearRight; x += 1) alpha[y * card.width + x] = 0;
  }
  const bounds = findAlphaBounds(alpha, card.width, card.height, cropLeft);
  const width = bounds.right - bounds.left;
  const height = bounds.bottom - bounds.top;
  const source = canvas(width, height);
  const sourceContext = source.getContext("2d", { willReadFrequently: true });
  if (!sourceContext) throw new Error("キャラ画像を準備できませんでした");
  highQuality(sourceContext);
  sourceContext.drawImage(card, bounds.left, bounds.top, width, height, 0, 0, width, height);
  const image = sourceContext.getImageData(0, 0, width, height);
  const croppedAlpha = new Uint8ClampedArray(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      croppedAlpha[y * width + x] = alpha[(y + bounds.top) * card.width + x + bounds.left];
    }
  }
  // The segmentation result is already soft. Let the matte finisher select the
  // silhouette directly; a full 3×3 blur erased small hair and costume detail.
  const finishedAlpha = finishAlphaMatte(croppedAlpha, width, height);
  for (let index = 0; index < finishedAlpha.length; index += 1) image.data[index * 4 + 3] = finishedAlpha[index];
  sourceContext.putImageData(image, 0, 0);
  return {
    heroSource: source,
    heroAlpha: finishedAlpha,
    hero: source,
    heroHead: findHead(finishedAlpha, width, height),
  };
}

function heroAccent(hero: HTMLCanvasElement) {
  const context = hero.getContext("2d", { willReadFrequently: true });
  if (!context) return "#95d9d4";
  const { data } = context.getImageData(0, 0, hero.width, hero.height);
  const bins = new Float64Array(24);
  for (let index = 0; index < hero.width * hero.height; index += 11) {
    const offset = index * 4;
    if (data[offset + 3] < 96) continue;
    const r = data[offset] / 255;
    const g = data[offset + 1] / 255;
    const b = data[offset + 2] / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    if (delta < 0.22 || max < 0.36) continue;
    let hue = 0;
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue = (hue * 60 + 360) % 360;
    bins[Math.floor(hue / 15) % bins.length] += delta * max;
  }
  let best = 0;
  for (let index = 1; index < bins.length; index += 1) if (bins[index] > bins[best]) best = index;
  return `hsl(${best * 15 + 7} 58% 70%)`;
}

async function decode(file: File) {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const result = canvas(bitmap.width, bitmap.height);
  const context = result.getContext("2d");
  if (!context) throw new Error("画像を開けませんでした");
  highQuality(context);
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  return result;
}

export async function preprocessFile(
  file: File,
  onProgress: (update: ProgressUpdate) => void,
): Promise<PreparedProject> {
  const started = performance.now();
  onProgress({ stage: "decode", progress: 0.03, message: "スクリーンショットを開いています…" });
  const source = await decode(file);
  const sourceWidth = source.width;
  const sourceHeight = source.height;
  onProgress({ stage: "detect", progress: 0.1, message: "2人・3人のプレートを見つけています…" });
  const rects = detectCards(source);
  const cards = rects.map((rect) => cropCard(source, rect));
  // The full phone screenshot is no longer needed once the cards are cropped.
  // Releasing its backing store before WASM inference saves 8–20 MB on iOS.
  source.width = 1;
  source.height = 1;

  const result = await segmentCards(cards, (progress, message) => {
    onProgress({
      stage: progress < 0.15 ? "model" : "segment",
      progress: 0.14 + progress * 0.72,
      message,
    });
  });
  if (result.masks.length !== cards.length) throw new Error("切り抜き結果が不足しています");

  onProgress({ stage: "layers", progress: 0.9, message: "投稿用にきれいに整えています…" });
  const preparedCards: PreparedCard[] = cards.map((card, index) => {
    const nameBox = detectNameBox(card);
    const hero = makeHero(card, new Uint8ClampedArray(result.masks[index]), nameBox);
    return {
      source: card,
      base: createBaseCard(card, nameBox),
      nameGlyphs: createNameLayer(card, nameBox),
      nameBox,
      ...hero,
      accent: heroAccent(hero.hero),
    };
  });
  onProgress({ stage: "layers", progress: 1, message: "編集準備ができました" });
  return {
    fileName: file.name,
    sourceWidth,
    sourceHeight,
    cards: preparedCards,
    modelMode: result.modelMode,
    preprocessMs: performance.now() - started,
  };
}
